import React, { Dispatch } from 'react';
import style from './writeToUsForm.module.scss';
import { Input } from "../Input/Input";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox } from "@kursk/components/ui/Checkbox/Checkbox";
import { useDispatch } from "react-redux";
import { notificationPush } from "@common/redux/slices/notification.slice";
import { useDispatchThunk } from "@common/redux/dispatchThunk";
import { writeToUsFormSlice, writeToUsThunk } from "@common/redux/slices/writeToUsForm.slice";
import { Textarea } from "@kursk/components/ui/Textarea/Textarea";
import { useSelectSlice } from "@common/redux/selectors/useSelectSlice";
import classNames from "classnames";
import { themeValue } from "@kursk/themes";
import { regionContent } from "@kursk/content/regionContent";
import { getConfig } from "@root/config/config";
import { WRITE_TO_US_FORM_VALIDATION_SCHEMA } from "@kursk/components/ui/WriteToUsForm/utils/formValidation";
import { DEFAULT_WRITE_TO_US_FORM_VALUES } from "@kursk/components/ui/WriteToUsForm/constants/constants";

interface IProps {
  inModal?: boolean;
  closeModal?: Dispatch<React.SetStateAction<boolean>> | null;
}

export const WriteToUsForm = ({ inModal = false, closeModal = null }: IProps) => {
  const { isSending } = useSelectSlice(writeToUsFormSlice);
  const dispatch = useDispatch();
  const dispatchThunk = useDispatchThunk();

  const { register, handleSubmit, formState, getValues, reset } = useForm({
    mode: 'onChange',
    resolver: yupResolver(WRITE_TO_US_FORM_VALIDATION_SCHEMA),
    defaultValues: DEFAULT_WRITE_TO_US_FORM_VALUES
  });

  const onSubmit = async () => {
    const formValues = getValues();

    const res = await dispatchThunk(writeToUsThunk({
      email: formValues.email,
      message: formValues.message.trim()
    }));

    if (!res.error) {
      dispatch(notificationPush({ content: 'Сообщение отправлено!', type: 'success' }));
      reset(DEFAULT_WRITE_TO_US_FORM_VALUES);
      if (inModal) {
        closeModal(false);
      }
    }
  }

  const getLogo = () => {
    const region = getConfig().theme;
    let src = '/images/big-logo-write-us-form.svg';

    if (['amur'].includes(region)) {
      src = "/images/big-logo-write-us-form.png"
    }

    return <img src={src} />;
  };

  return (
    <div className={classNames(inModal ? style.wrapperInModal : style.wrapper)}>
      <div className={style.form}>
        {themeValue({
          bel: <div className={inModal ? style.titleModal : style.title}>
            {inModal ? 'напишите нам' : 'не нашли ответ на вопрос?'}
          </div>,
          default: <div className={style.title}>
            {inModal ? 'напишите нам' : 'не нашли ответ на вопрос?'}
          </div>
        })}
        <div className={inModal ? style.subTitleModal : style.subTitle}>
          {inModal ? 'Принимаем вопросы, жалобы, уточнения и предложения по улучшению'
            : 'Спросите напрямую - ответим лично'}
        </div>
        <div className={style.wrapperInner}>
          <label className={style.textLabel}>
            {inModal ? 'Сообщение' : 'Ваш вопрос'}
          </label>
          <div className={style.inputRow}>
            <Textarea
              {...register('message')}
              errors={formState.errors}
              rows={10}
              className={style.input}
              areaClassName={style.area}/>
          </div>
          <label className={classNames(style.emailLabel, inModal && style.emailLabel_modal)}>Email для
            ответа</label>
          <div className={style.inputRow}>
            <Input {...register('email')} errors={formState.errors}/>
          </div>
          <div className={style.checkboxWrapper}>
            <div>
              <Checkbox {...register('agree')} errors={formState.errors}>
                <span className={style.agreementText}>Я подтверждаю свое согласие с условиями
                  <a className={'withLine'} target="_blank" href={regionContent.agreementLink} rel="noreferrer">
                    {` пользовательского соглашения`}
                  </a>
                </span>
              </Checkbox>
            </div>
            <div>
              <Checkbox {...register('agreePolicy')} errors={formState.errors}>
                <span className={style.agreementText}>Я подтверждаю свое согласие с
                  <a className={'withLine'} target="_blank" href={regionContent.policyLink} rel="noreferrer">
                    {` политикой обработки персональных данных`}
                  </a>
                </span>
              </Checkbox>
            </div>
          </div>
          <button className={style.button} onClick={handleSubmit(onSubmit)} disabled={isSending}>Отправить</button>
        </div>
      </div>
      {!inModal && (
        <div className={style.logo}>
          {getLogo()}
        </div>
      )}
    </div>
  );
};
